import {ProductPrice, useProductOptions} from '@shopify/hydrogen';
import clsx from 'clsx';
import type {
  ProductWithNodes,
  SanityProductPage,
  SanitySettings,
} from '../../types';
import {hasMultipleProductOptions} from '../../utils/productOptions';
import SelectedVariantBuyNowButton from './buttons/SelectedVariantBuyNow.client';
import SelectedVariantAddToCartButton from './buttons/SelectedVariantAddToCart.client';
import ProductOptions from './options/ProductOptions.client';
import PdfPattern from './information/PdfPattern.client';
import Difficulty from './information/Metafields.client';
import {useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {getSanityLocale} from '../../utils/getSanityLocale';
import Material from './information/Material.client';
import Button from '../elements/Button';
import {useMadeToOrderStore} from '../../hooks/useMadeToOrderStore';
import translate from '../../utils/getTranslation';
import Table from './information/Table';

type Props = {
  sanityProduct: SanityProductPage;
  storefrontProduct: ProductWithNodes;
  settings?: SanitySettings;
};

function ProductPrices({
  storefrontProduct,
}: {
  storefrontProduct: ProductWithNodes;
}) {
  const {selectedVariant} = useProductOptions();

  if (!storefrontProduct || !selectedVariant) {
    return null;
  }

  return (
    <div className="flex text-lg leading-none">
      <ProductPrice
        className="mr-2 text-darkGray line-through decoration-red"
        data={storefrontProduct}
        priceType="compareAt"
        variantId={selectedVariant.id}
      />
      <ProductPrice data={storefrontProduct} variantId={selectedVariant.id} />
    </div>
  );
}

export default function ProductWidget({
  storefrontProduct,
  settings,
  sanityProduct,
}: Props) {
  const sanityLocale = getSanityLocale();
  const {setProductTitle, toggleOpen} = useMadeToOrderStore();

  const {selectedVariant} = useProductOptions();
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );

  const availableForSale = selectedVariant?.availableForSale;
  const productDescription = storefrontProduct.descriptionHtml;

  // Product Information Tab
  const [tabIndex, setTabIndex] = useState(0);
  const TAB_HEADERS_EN = [
    'Difficulty',
    'Materials',
    'Measurements',
    'PDF Pattern',
  ];
  const TAB_HEADERS_NB = [
    'Vanskelighet',
    'Materialer',
    'Måltabell',
    'PDF Mønster',
  ];

  let TAB_HEADERS;

  if (sanityLocale === 'no') {
    TAB_HEADERS = TAB_HEADERS_NB;
  } else {
    TAB_HEADERS = TAB_HEADERS_EN;
  }

  const CONTAINER = {
    hidden: {opacity: 0, y: -5},
    show: {
      opacity: 1,
      y: 0,
      transition: {
        delayChildren: 0.1,
        staggerChildren: 0.1,
        duration: 0.7,
        ease: 'easeOut',
      },
    },
  };

  const ITEM = {
    hidden: {opacity: 0},
    show: {opacity: 1},
  };

  if (!selectedVariant) {
    return null;
  }

  if (!storefrontProduct.metafields) return null;

  const hasOptionType = () => {
    // @ts-ignore - TS doesn't know about selectedOptions
    return selectedVariant.selectedOptions?.find(({name}) => name === 'Type');
  };

  const optionType = hasOptionType()?.value;

  const SIZE_CHART_LABEL = translate('SIZE_CHART_LABEL');
  const NEWS = translate('NEWS');
  const EXTENSION = translate('EXTENSION');
  const BOOK = translate('BOOK');

  return (
    <motion.div
      variants={CONTAINER}
      initial="hidden"
      animate="show"
      className={clsx(
        'pointer-events-auto z-10 mx-auto',
        'w-full max-w-[40rem] px-4 pb-8 pt-8 md:px-8 md:pt-20',
      )}
    >
      <div className="flex gap-2">
        {/* Sold out */}
        {!availableForSale && (
          <div className="text-xs uppercase text-neutral-500">Sold out</div>
        )}

        {/* Sale */}
        {availableForSale && selectedVariant?.compareAtPriceV2 && (
          <div className="text-xs uppercase text-red">Sale</div>
        )}

        {availableForSale && storefrontProduct.tags?.includes('Book') && (
          <div className="mb-4 flex">
            <p className="rounded-xs bg-neutral-300 p-1.5 text-xs leading-[.8] text-neutral-900">
              {BOOK}
            </p>
          </div>
        )}

        {availableForSale && storefrontProduct.tags?.includes('news') && (
          <div className="mb-4 flex">
            <p className="rounded-xs bg-neutral-900 p-1.5 text-xs leading-[.8] text-neutral-100">
              {NEWS}
            </p>
          </div>
        )}

        {availableForSale &&
          storefrontProduct.tags?.includes('mønstertillegg') && (
            <div className="mb-4 flex">
              <p className="rounded-xs bg-neutral-300 p-1.5 text-xs leading-[.8] text-neutral-900">
                {EXTENSION}
              </p>
            </div>
          )}
      </div>

      <div className="mb-6 justify-between md:mb-3 md:flex md:items-end">
        {/* Title */}
        {storefrontProduct?.title &&
          (storefrontProduct.tags?.includes('Book') ? (
            <h1
              className={clsx(
                'mb-8 translate-y-[.28em] text-center font-kormelink leading-none md:mb-1 md:text-left',
                storefrontProduct.title.length > 10 ? 'text-2xl' : 'text-3xl',
              )}
            >
              {storefrontProduct.title}
            </h1>
          ) : (
            <h1
              className={clsx(
                'mb-8 translate-y-[.28em] text-center font-kormelink leading-none md:mb-1 md:text-left',
                storefrontProduct.title.length > 10 ? 'text-2xl' : 'text-3xl',
              )}
            >
              <span className="uppercase">
                {storefrontProduct.title.indexOf(' ') !== -1
                  ? storefrontProduct.title.slice(
                      0,
                      storefrontProduct.title.indexOf(' '),
                    )
                  : storefrontProduct.title}
                <br />
              </span>
              {storefrontProduct.title.indexOf(' ') !== -1
                ? storefrontProduct.title.slice(
                    storefrontProduct.title.indexOf(' ') + 1,
                  )
                : ''}
            </h1>
          ))}

        {/* Prices */}
        <div className="flex flex-col items-center justify-center text-center md:items-end md:text-right">
          {optionType && (
            <p className="mb-1 text-sm text-neutral-400">{optionType}</p>
          )}
          <ProductPrices storefrontProduct={storefrontProduct} />
        </div>
      </div>

      {/* Divider */}
      {/* <div className="w-full border-b border-stone-200" /> */}

      {/* Product options */}
      <motion.div variants={ITEM} className="">
        {multipleProductOptions && <ProductOptions />}
      </motion.div>

      {/* Product actions */}
      <motion.div variants={ITEM} className="grid gap-2 md:grid-cols-2">
        <SelectedVariantAddToCartButton
          label={translate('ADD_TO_CART')}
          className="rounded-xs bg-neutral-300 p-3 leading-none text-neutral-900"
        />
        <SelectedVariantBuyNowButton className="rounded-xs bg-neutral-800 p-3 text-sm leading-none text-neutral-100" />

        {!storefrontProduct.tags?.includes('Book') && (
          <Button
            type="button"
            className="mx-auto w-auto bg-transparent p-3 leading-none text-neutral-500 md:col-span-2"
            onClick={() => {
              storefrontProduct.title &&
                setProductTitle(storefrontProduct.title);
              toggleOpen();
            }}
          >
            {translate('MADE_TO_ORDER_BUTTON')}
          </Button>
        )}
      </motion.div>

      <div className="mt-8">
        {/* Description */}
        {productDescription && (
          <motion.div variants={ITEM} className="mb-8 ">
            <p className="mb-2 text-lg">{translate('DESCRIPTION')}</p>
            <div
              dangerouslySetInnerHTML={{__html: productDescription}}
              className="product-description text-sm [&>p:not(:last-child)]:mb-2"
            />
          </motion.div>
        )}

        {/* Information Tabs */}
        {!storefrontProduct.tags?.includes('Book') && (
          <motion.div variants={ITEM}>
            <div className="relative text-neutral-400">
              <div className="flex w-full gap-6 overflow-scroll  text-sm scrollbar-hide">
                {TAB_HEADERS.map((header, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setTabIndex(index);
                    }}
                    className={clsx(
                      'whitespace-nowrap py-3',
                      tabIndex == index && 'text-neutral-900',
                    )}
                  >
                    {header}
                  </button>
                ))}
              </div>
            </div>
            <div className="">
              <div className="border-t border-neutral-300">
                {tabIndex === 0 && (
                  <AnimatePresence>
                    <motion.div
                      initial={{y: -5, opacity: 0}}
                      animate={{y: 0, opacity: 1}}
                      exit={{y: 5, opacity: 0}}
                      transition={{duration: 0.25}}
                    >
                      <Difficulty storefrontProduct={storefrontProduct} />
                    </motion.div>
                  </AnimatePresence>
                )}
                {tabIndex === 1 && (
                  <AnimatePresence>
                    <motion.div
                      initial={{y: -5, opacity: 0}}
                      animate={{y: 0, opacity: 1}}
                      exit={{y: 5, opacity: 0}}
                      transition={{duration: 0.25}}
                    >
                      <Material storefrontProduct={storefrontProduct} />
                      {sanityProduct.fabricChart && (
                        <Table table={sanityProduct.fabricChart} />
                      )}
                    </motion.div>
                  </AnimatePresence>
                )}
                {tabIndex === 2 && settings?.sizeChart && (
                  <AnimatePresence>
                    <motion.div
                      initial={{y: -5, opacity: 0}}
                      animate={{y: 0, opacity: 1}}
                      exit={{y: 5, opacity: 0}}
                      transition={{duration: 0.25}}
                    >
                      <Table
                        table={settings.sizeChart}
                        label={SIZE_CHART_LABEL}
                      />
                    </motion.div>
                  </AnimatePresence>
                )}
                {tabIndex === 3 && settings?.pdfPattern && (
                  <AnimatePresence>
                    <motion.div
                      initial={{y: -5, opacity: 0}}
                      animate={{y: 0, opacity: 1}}
                      exit={{y: 5, opacity: 0}}
                      transition={{duration: 0.25}}
                    >
                      <PdfPattern {...settings} />
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
